import { envConfig } from '@src/utils/envConfig';
import { AuthContext, eraseCookie, readCookie, urlFromLocalOrAbsolute } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

const sessionCookie = envConfig.VITE_SESSION_COOKIE;
export interface AuthSession {
  expires_at: number;
}

export function GCashAuthContextProvider({ children }: PropsWithChildren<{ session?: AuthSession }>) {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] = useState(false);

  const logout = useCallback(() => {
    eraseCookie(sessionCookie);
    // TODO(robertz): need to logout properly using gcash proper auth services.
  }, []);

  const login = useCallback(async () => {
    // step:0, if in the super app, we're already authenticated thanks to /login.html.
    if (readCookie(sessionCookie)) {
      setAuthenticated(true);
      return;
    }

    const href = window.location.href;
    const url = new URL(href);
    const origin = url.origin;
    const token = url.searchParams.get('token');

    // step:1, the super app might also navigate directly here (instead of using the /login.html page) but will need to provide a token directly.
    // dummy login is gated by config with a fallback user configured on the backend.
    if (!token) {
      if (envConfig.VITE_ALLOW_DUMMY_LOGIN !== 'true') {
        window.location.replace(urlFromLocalOrAbsolute(`${origin}/unauthorized`));
        return;
      }

      const loginUrl = `${origin}/auth/login?auth_code=${encodeURIComponent(
        // just do hello_world as a placeholder
        'hello_world'
      )}&request_type=token&next=${origin}`;
      const newUrl = new URL(urlFromLocalOrAbsolute(loginUrl));
      window.location.replace(urlFromLocalOrAbsolute(newUrl.toString()));
      return;
    }
    // step:2 we got a token, now we need to exchange it for a session cookie
    const authTokenUrl = `${origin}/auth/token?token=${token}&next=${origin}`;
    window.location.replace(urlFromLocalOrAbsolute(authTokenUrl));
  }, []);

  // Check if the cookie exist, and login if possible, otherwise, do nothing and let the user get the cookie from the login flow.
  useEffect(() => {
    if (!hasAttemptedAutoLogin) {
      login();
      setHasAttemptedAutoLogin(true);
    }
  }, [login, hasAttemptedAutoLogin]);

  const value = useMemo(
    () => ({ isAuthenticated, logout, login, hasAttemptedAutoLogin }),
    [isAuthenticated, logout, hasAttemptedAutoLogin, login]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
